import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';

echarts.use([TooltipComponent, GridComponent, BarChart]);

type ProductsData = {
  createdAt: string;
};

type TimeRange = 'today' | 'week' | 'month' | 'year';

type Props = {
  data: ProductsData[];
  timeRange: TimeRange;
};

const EcomTotalProductChart = ({ data, timeRange }: Props) => {
  const { getThemeColor } = useAppContext();

  // Generate time keys for x-axis based on timeRange
  const generateTimeKeys = (): string[] => {
    const now = dayjs();
    switch (timeRange) {
      case 'today':
        // Generate 3-hour interval keys: 0,3,6,...,21 (8 buckets)
        return Array.from({ length: 8 }, (_, i) =>
          now
            .hour(i * 3)
            .minute(0)
            .second(0)
            .format('YYYY-MM-DD HH')
        );
      case 'week':
        return Array.from({ length: 7 }, (_, i) =>
          now.subtract(6 - i, 'day').format('YYYY-MM-DD')
        );
      case 'month':
        return Array.from({ length: 30 }, (_, i) =>
          now.subtract(29 - i, 'day').format('YYYY-MM-DD')
        );
      case 'year':
        return Array.from({ length: 12 }, (_, i) =>
          now.month(i).format('YYYY-MM')
        );
      default:
        return [];
    }
  };

  // Aggregate counts based on time keys & timeRange
  const { timeKeys, counts } = useMemo(() => {
    const keys = generateTimeKeys();
    const countMap: Record<string, number> = {};

    data.forEach(({ createdAt }) => {
      const date = dayjs(createdAt);
      let key = '';

      if (timeRange === 'today') {
        // Group hours into 3-hour buckets (0,3,6,...)
        const bucketHour = Math.floor(date.hour() / 3) * 3;
        key = date.hour(bucketHour).minute(0).second(0).format('YYYY-MM-DD HH');
      } else if (timeRange === 'week' || timeRange === 'month') {
        key = date.format('YYYY-MM-DD');
      } else if (timeRange === 'year') {
        key = date.format('YYYY-MM');
      }

      if (key) {
        countMap[key] = (countMap[key] || 0) + 1;
      }
    });

    // Map counts to time keys (fill missing with 0)
    return {
      timeKeys: keys,
      counts: keys.map(k => countMap[k] || 0)
    };
  }, [data, timeRange]);

  // xAxis configuration based on timeRange
  const xAxisConfig = useMemo(() => {
    const common = {
      type: 'category' as const,
      data: timeKeys,
      boundaryGap: true,
      axisLine: { lineStyle: { color: getThemeColor('secondary-bg') } },
      axisTick: { show: false },
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontSize: 10,
        rotate: ['month', 'year', 'week'].includes(timeRange) ? 30 : 0,
        align: 'center',
        interval: 'auto', // automatic spacing
        formatter: (val: string) => {
          switch (timeRange) {
            case 'today':
              return dayjs(val, 'YYYY-MM-DD HH').format('HH');
            case 'week':
              return dayjs(val).format('DD MMM');
            case 'month':
              return dayjs(val).format('DD MMM');
            case 'year':
              return dayjs(val, 'YYYY-MM').format('MMM');
            default:
              return val;
          }
        }
      }
    };

    switch (timeRange) {
      case 'today':
        return {
          ...common,
          axisLabel: {
            ...common.axisLabel,
            formatter: (val: string) =>
              dayjs(val, 'YYYY-MM-DD HH').format('HH'), // Show hour like '00', '03', etc
            interval: 0 // Show every label (8 labels only, no clutter)
          }
        };
      case 'week':
        return {
          ...common,
          axisLabel: {
            ...common.axisLabel,
            formatter: (val: string) => dayjs(val).format('DD MMM'),
            interval: 0
          }
        };
      case 'month':
        return {
          ...common,
          axisLabel: {
            ...common.axisLabel,
            formatter: (val: string) => dayjs(val).format('DD-MMM'),
            rotate: 0
          }
        };
      case 'year':
        return {
          ...common,
          axisLabel: {
            ...common.axisLabel,
            formatter: (val: string) => dayjs(val, 'YYYY-MM').format('MMM'),
            interval: 0
          }
        };
      default:
        return common;
    }
  }, [timeRange, timeKeys, getThemeColor]);

  const options = {
    color: getThemeColor('primary'),
    tooltip: {
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('light-text-emphasis') },
      borderWidth: 1,
      transitionDuration: 0,
      formatter: ({ name, value }: { name: string; value: number }) => {
        let dateStr = '';
        if (timeRange === 'today') {
          dateStr = dayjs(name, 'YYYY-MM-DD HH').format('HH:mm, DD MMM YYYY');
        } else if (timeRange === 'year') {
          dateStr = dayjs(name, 'YYYY-MM').format('MMM YYYY');
        } else {
          dateStr = dayjs(name).format('DD MMM YYYY');
        }
        return `<strong>${dateStr}:</strong> ${value} product(s)`;
      }
    },
    xAxis: xAxisConfig,
    yAxis: {
      type: 'value',
      show: false
    },
    series: [
      {
        type: 'bar',
        barWidth: '8px',
        data: counts,
        showBackground: true,
        itemStyle: { borderRadius: 10 },
        backgroundStyle: {
          borderRadius: 10,
          color: getThemeColor('primary-bg-subtle')
        }
      }
    ],
    grid: { right: 10, left: 10, bottom: 30, top: 10 }
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={options}
      style={{ height: 200, width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
      theme={'light'}
    />
  );
};

export default EcomTotalProductChart;
